import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Zap, 
  Send,
  ArrowLeft,
  LogOut,
  Users,
  MessageCircle,
  Sun,
  Home
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import Volti from '../components/Volti';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CommunityChat = () => {
  const navigate = useNavigate();
  const { communityId } = useParams();
  const { user, logout, isAuthenticated } = useAuth();
  
  const [community, setCommunity] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated && communityId) {
      fetchData();
      // Poll for new messages every 5 seconds
      const interval = setInterval(fetchMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, communityId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [commRes, msgRes] = await Promise.all([
        axios.get(`${API_URL}/api/communities/${communityId}`),
        axios.get(`${API_URL}/api/chat/${communityId}/messages`, { headers })
      ]);
      setCommunity(commRes.data);
      setMessages(msgRes.data);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 404) {
        navigate('/communities');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API_URL}/api/chat/${communityId}/messages`, { headers });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API_URL}/api/chat/${communityId}/messages`, {
        content: newMessage
      }, { headers });
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/communities/${communityId}`)}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-semibold text-foreground">{community?.name}</h1>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {community?.member_count} membres
                </p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 pt-20 pb-24 px-4">
        <div className="max-w-4xl mx-auto">
          {messages.length === 0 ? (
            <div className="text-center py-20">
              <div className="flex justify-center mb-4">
                <Volti message="" position="inline" size="lg" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">Aucun message</h3>
              <p className="text-muted-foreground">Soyez le premier à écrire ! Volti est là pour vous accompagner.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, index) => {
                const isMe = msg.user_id === user?.id;
                return (
                  <div
                    key={msg.id || index}
                    className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] ${isMe ? 'order-2' : ''}`}>
                      {!isMe && (
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                            msg.user_type === 'producer' ? 'bg-yellow-100' : 'bg-blue-100'
                          }`}>
                            {msg.user_type === 'producer' ? (
                              <Sun className="w-3 h-3 text-yellow-600" />
                            ) : (
                              <Home className="w-3 h-3 text-blue-600" />
                            )}
                          </div>
                          <span className="text-xs font-medium text-muted-foreground">
                            {msg.user_name}
                          </span>
                        </div>
                      )}
                      <div
                        className={`px-4 py-3 rounded-2xl ${
                          isMe
                            ? 'bg-primary text-white rounded-br-md'
                            : 'bg-muted rounded-bl-md'
                        }`}
                      >
                        <p className="text-sm">{msg.content}</p>
                      </div>
                      <p className={`text-xs text-muted-foreground mt-1 ${isMe ? 'text-right' : ''}`}>
                        {new Date(msg.created_at).toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </main>

      {/* Input */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4">
        <form onSubmit={handleSend} className="max-w-4xl mx-auto flex gap-3">
          <Input
            type="text"
            placeholder="Écrire un message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            className="flex-1 rounded-full"
            disabled={sending}
          />
          <Button
            type="submit"
            size="icon"
            className="rounded-full w-12 h-12"
            disabled={sending || !newMessage.trim()}
          >
            <Send className="w-5 h-5" />
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CommunityChat;
